import {Trans} from '@lingui/macro';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {TextBox} from '../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {ButtonGroupSpacer} from '../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {Dialog} from '../../../components/common/dialogs/dialog';
import {DialogContent} from '../../../components/common/dialogs/dialogContent';
import {DialogFooter} from '../../../components/common/dialogs/dialogFooter';
import {DialogHeader} from '../../../components/common/dialogs/dialogHeader';
import {useLinkNavigation} from '../../../components/common/navigation/useLinkNavigation';
import {useAppDispatch} from '../../../helpers/reduxHelpers';
import {LocalApplicationChannelTypeConfigModel} from '../../../models/api/localApplicationModel';
import {buildSettingsDevelopersPath} from '../../../models/url/settingsUrlHelpers';
import {SettingsDevelopersPagesEnum, SettingsRootPagesEnum} from '../../../models/url/settingsUrlModel';
import {deleteLocalApplicationChannelTypeAsyncAction} from '../../../store/data/localApplications/localApplicationsActions';
import {SettingsBannersEnum, useSettingsBanners} from '../../banners/settingsBannersContext';

/*
 * Constant.
 */

const deleteToken = 'DELETE';

/*
 * Props.
 */

interface SettingsDevelopersDeleteFeatureChannelDialogProps {
  localApplicationId: number;
  channelType: LocalApplicationChannelTypeConfigModel;
  onRequestClose: Handler;
}

/*
 * Style.
 */

const StyledButtonGroup = styled(ButtonGroup)`
  width: 100%;
`;

/*
 * Component.
 */

export const SettingsDevelopersDeleteFeatureChannelDialog: FC<
  SettingsDevelopersDeleteFeatureChannelDialogProps
> = (props) => {
  const {localApplicationId, channelType, onRequestClose} = props;
  const [deleteTextInput, setDeleteTextInput] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const {showBanner} = useSettingsBanners();
  const dispatch = useAppDispatch();
  const {navigateToLink} = useLinkNavigation();

  const onCloseDialog = () => {
    // If we are deleting, prevent closing.
    if (isDeleting) {
      return;
    }
    onRequestClose();
  };

  const onDeleteClickAsync = async () => {
    setIsDeleting(true);
    try {
      await dispatch(deleteLocalApplicationChannelTypeAsyncAction(localApplicationId, channelType.id));
      navigateToLink(
        buildSettingsDevelopersPath({
          rootPage: SettingsRootPagesEnum.DEVELOPERS,
          subPage: SettingsDevelopersPagesEnum.APPLICATION_FEATURES,
          applicationId: localApplicationId,
        }),
      );
      onRequestClose();
    } catch {
      showBanner({
        type: SettingsBannersEnum.ERROR,
        message: (
          <Trans id="settings-developers-delete-feature-channel-error-banner">
            Error deleting channel. Please try again.
          </Trans>
        ),
      });
      setIsDeleting(false);
    }
  };

  return (
    <Dialog onRequestClose={onCloseDialog}>
      <DialogHeader
        onRequestClose={onCloseDialog}
        title={
          <Trans id="settings-developers-delete-feature-channel-dialog-title">Delete channel type</Trans>
        }
      />
      <DialogContent>
        <Trans id="settings-developers-delete-feature-channel-dialog-description-disclaimer">
          Are you sure you want to permanently delete this channel type? Any instances of this channel type
          that have been created will no longer be able to send or receive messages. Type {deleteToken} to
          confirm this action.
        </Trans>
        <TextBox value={deleteTextInput} onChange={setDeleteTextInput} placeholder={deleteToken} />
      </DialogContent>
      <DialogFooter>
        <StyledButtonGroup>
          <ButtonGroupSpacer />
          <Button onClick={onCloseDialog} isDisabled={isDeleting}>
            <Trans id="settings-developers-delete-feature-channel-dialog-cancel-button">Cancel</Trans>
          </Button>
          <Button
            variant={ButtonVariantsEnum.DANGER}
            onClick={onDeleteClickAsync}
            isDisabled={deleteTextInput !== deleteToken || isDeleting}
          >
            <Trans id="settings-developers-delete-feature-channel-dialog-delete-button">Delete</Trans>
          </Button>
        </StyledButtonGroup>
      </DialogFooter>
    </Dialog>
  );
};
